/**
 * ExitGate — the gate half of the switch→gate pair (Brief 014). N++'s locked exit door:
 * a SOLID barrier while its switch is unhit, non-solid (open) once any EXITSWITCH sharing
 * `props.linkId` is touched.
 *
 * The open state is read from the World latch (ctx.latchOn) and cached in `update` —
 * which runs BEFORE World's solid pass — so `solidBody` reports the right state this
 * step. Since the latch is cleared on respawn, a death re-closes the gate.
 *
 * Determinism: a boolean latch read, no math → ESLint-clean. Extends UP from the placed
 * cell (a door standing on the floor).
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

export class ExitGate implements Entity {
  readonly type = EntityType.EXITGATE;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private linkId: number;
  private open = false;

  /** @param props `linkId` (int; default 1), `w`/`h` in tiles (default 1×3, a tall door
   *               rising UP from the placed cell). */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    const w = posInt(props?.w, 1);
    const h = posInt(props?.h, 3);
    this.body = { x: cx - ts / 2, y: feetY - h * ts, w: w * ts, h: h * ts };
    const raw = props?.linkId;
    const id = typeof raw === 'number' ? raw : Number(raw ?? 1);
    this.linkId = Number.isFinite(id) ? Math.floor(id) : 1;
  }

  /** Barrier while closed; null (pass-through) once the switch opens it. */
  solidBody(): AABB | null { return this.open ? null : this.body; }

  update(_dt: number, ctx: EntityContext): void {
    this.open = ctx.latchOn(this.linkId);
  }

  onPlayerOverlap(_ctx: EntityContext): void { /* solid — acts via solidBody */ }

  spriteKey(): string { return this.open ? 'exitgate_open' : 'exitgate'; }

  visible(): boolean { return true; }

  renderSize(): { w: number; h: number } { return { w: this.body.w, h: this.body.h }; }
}

function posInt(v: unknown, dflt: number): number {
  return typeof v === 'number' && v >= 1 ? Math.floor(v) : dflt;
}
